import { useEffect, useMemo, useRef, useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import { useT } from "../../i18n/LanguageProvider";
import { t as translate } from "../../i18n/t";
import type { PlainKey } from "../../i18n/types";
import { printRaster } from "../../platform/printer";
import {
  resolvePrinterFor,
  type ResolvedPrinter,
} from "../../platform/printerRegistry";
import { tuningToJobOptions } from "../../platform/printSettings";
import { toCatPrinterJob } from "../../receipt/catPrinter";
import { NOTE_FONTS, noteFont } from "../../receipt/noteFonts";
import {
  DEFAULT_SIZE_PX,
  SIZE_LADDER,
  type NoteOrientation,
} from "../../receipt/noteLayout";
import { renderNote } from "../../receipt/noteRender";
import {
  DICTATION_LOCALES,
  dictationSupported,
  isDictationLocale,
  type DictationLocale,
} from "../../platform/dictation";
import { DictationButton } from "../ui/DictationButton";
import { Select } from "../ui/Select";
import { NotePreview } from "./NotePreview";
import { ShopFloorPrinterChips } from "./ShopFloorPrinterChips";
import { ShopFloorIcon } from "./ShopFloorIcon";

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * G-65 — "Print a note": the gift message a florist writes for a customer
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * WHAT A FLORIST IS ACTUALLY DOING when this screen is open: standing at the
 * counter, often with the customer in front of them, holding a physical card
 * whose size they can see and this app cannot. Everything here follows from
 * that.
 *
 *   - The message box is the biggest thing on the screen and takes focus on
 *     arrival. Nobody should hunt for where to type.
 *   - The preview is TRUE SIZE, beside the box, updating as they type — so the
 *     question they actually have ("will this fit the card in my hand?") is
 *     answered by holding the card up to the glass.
 *   - The text SURVIVES a print. A second copy, or a typo spotted on paper, is
 *     one tap away rather than a retype.
 *   - Nothing is destroyed without saying so: Clear is a plain button, sitting
 *     apart from the primary action.
 *
 * Orientation is the florist's call, not ours, because only they can see the
 * card — which is exactly why it is a visible toggle and not a clever guess.
 */

/**
 * The two card shapes, drawn rather than named (G-107). "Vertical" and
 * "Horizontal" were words for something the florist decides by LOOKING at the
 * card in their hand, so the choice is now the shape itself. The words survive
 * as each button's accessible name, looked up at render — a module-level array
 * of labels would hold whichever language was active at import (the trap G-85
 * S2 paid for with `FOB_ANTENNA_HINT`), which is why this holds KEYS.
 */
const ORIENTATIONS: Array<{ value: NoteOrientation; labelKey: PlainKey }> = [
  { value: "vertical", labelKey: "note.vertical" },
  { value: "horizontal", labelKey: "note.horizontal" },
];

/** A blank card, tall or wide — the house icon idiom: 24 px, stroke-only, 1.8. */
function CardGlyph({ orientation }: { orientation: NoteOrientation }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {orientation === "vertical" ? (
        <rect x="6.5" y="3" width="11" height="18" rx="2" />
      ) : (
        <rect x="3" y="6.5" width="18" height="11" rx="2" />
      )}
    </svg>
  );
}

/** Long enough for a real message, short enough to stay a NOTE and not a letter. */
const MAX_CHARS = 400;

/**
 * Which language the microphone listens in, remembered per TABLET.
 *
 * Per-device is the interim answer, not the intended one: the language belongs
 * to the person, and the kiosk already knows who is at the glass (the floor
 * token carries the individual florist, not the manager who armed the tablet).
 * Moving it onto the account needs a User extension table and a Team-page
 * control, which is its own work order — this ships the microphone first.
 *
 * Spanish is the default because that is the counter's language. On THIS screen
 * the default is wrong more often than elsewhere, which is why the control
 * beside it is a visible select rather than a chip: the words being dictated
 * belong to a customer, not to the florist.
 */
const DICTATION_LANG_KEY = "fw-dictation-lang";
const DEFAULT_DICTATION_LOCALE: DictationLocale = "es-ES";

function storedDictationLocale(): DictationLocale {
  try {
    const raw = window.localStorage.getItem(DICTATION_LANG_KEY);
    if (raw !== null && isDictationLocale(raw)) return raw;
  } catch {
    // Blocked storage: the default is right most of the time anyway.
  }
  return DEFAULT_DICTATION_LOCALE;
}

function rememberDictationLocale(locale: DictationLocale): void {
  try {
    window.localStorage.setItem(DICTATION_LANG_KEY, locale);
  } catch {
    // Honoured this session, forgotten on restart — the printer registry's rule.
  }
}

/**
 * Pulls the florist's chosen size onto a rung this FACE can actually print.
 *
 * `steps` is descending. A wish above the top or below the floor is clamped to
 * it; a wish between rungs (the ladder changed under it, because the face did)
 * snaps DOWN to the nearest real rung rather than up — the handwriting face
 * breaks up below 30 px, so erring small is the one direction that cannot be
 * erred in.
 */
function clampToSizeSteps(wish: number, steps: number[]): number {
  if (steps.length === 0) return wish;
  const floor = steps[steps.length - 1];
  if (wish >= steps[0]) return steps[0];
  if (wish <= floor) return floor;
  return steps.find((px) => px <= wish) ?? floor;
}

/*
 * G-97 (owner, 2026-09-20): `canPairPrinter` is DELETED, not widened. It
 * allowed manager and above, and its own comment conceded it enforced
 * nothing — pairing is Bluetooth plus this tablet's localStorage, with no
 * model, no endpoint and no permission behind it. What it did achieve was a
 * florist alone at the counter, holding a gift note, unable to print it. The
 * prompt and its button live in `PairPrinterPrompt` (box label, item
 * sticker); this screen carries `ShopFloorPrinterChips` instead (G-155),
 * which pairs AND forgets both roles.
 */

export function ShopFloorNotePanel() {
  const { t } = useT();
  const [text, setText] = useState("");
  const [fontId, setFontId] = useState(NOTE_FONTS[0].id);
  const [orientation, setOrientation] = useState<NoteOrientation>("vertical");
  /**
   * The size the florist ASKED for, which is not always the size they get: the
   * face's floor clamps it, and auto-fit still shrinks below it when a long
   * message will not fit across the paper.
   *
   * Held as the WISH rather than corrected in place. Switching to the
   * handwriting face clamps a 24 px choice up to its 30 px floor; switching
   * back should return the 24 they picked, not the 30 that face forced.
   */
  const [sizeWish, setSizeWish] = useState(DEFAULT_SIZE_PX);
  /**
   * Whichever machine a note will come out of — the dedicated sticker printer
   * if this counter has one, otherwise the receipt printer. Most shops run one
   * printer and swap the roll, so the common case needs no setup at all.
   */
  const [printer, setPrinter] = useState<ResolvedPrinter | null>(() =>
    resolvePrinterFor("notes"),
  );
  const [fontReady, setFontReady] = useState(false);
  const [dictationLocale, setDictationLocale] = useState<DictationLocale>(() =>
    storedDictationLocale(),
  );
  const [busy, setBusy] = useState<"print" | "pair" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const box = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    // The florist came here to write. Put the cursor where they are going.
    box.current?.focus();
  }, []);

  /**
   * Wait for a downloaded face before drawing a single pixel in it.
   *
   * THE QUIETEST BUG IN THE WHOLE FEATURE. Canvas `measureText` and `fillText`
   * do NOT wait for a webfont — they silently use the fallback and report its
   * metrics. The preview would look right, the layout would be measured against
   * the wrong face, and the sticker would come out in plain lettering with the
   * florist none the wiser. So the font is loaded first and the render is held
   * until it is ready.
   *
   * `renderNote` stays SYNCHRONOUS on purpose: making it async would break the
   * coalesced redraw and the preview-is-the-print property. The waiting happens
   * here instead, once per face.
   */
  useEffect(() => {
    const face = noteFont(fontId);
    /**
     * `document.fonts` is not guaranteed. Reading `.load` off `undefined`
     * throws SYNCHRONOUSLY, which the `.catch()` below cannot intercept — it
     * only sees rejections — so on a WebView without the CSS Font Loading API
     * the error would escape to React and blank a kiosk screen that sits behind
     * a PIN. `minSdkVersion` is 24, and the same page opens in whatever desktop
     * browser staff have. Flagged by `/code-review` 2026-09-12.
     */
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    if (!face.webFont || !fonts) {
      setFontReady(true);
      return;
    }
    let cancelled = false;
    setFontReady(false);
    // The weight and size come from the face itself, not from literals that
    // have to be kept in step with it and with the `@font-face` block.
    void fonts
      .load(`${face.weight} ${SIZE_LADDER[0]}px "${face.webFont}"`)
      .then(() => {
        if (!cancelled) setFontReady(true);
      })
      .catch(() => {
        // The download failed. Draw anyway rather than stranding the florist:
        // the stack falls back to a system hand, and what the preview shows is
        // still exactly what will print.
        if (!cancelled) setFontReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [fontId]);

  /** The rungs the chosen face prints cleanly at, largest first. */
  const sizeSteps = useMemo(
    () => SIZE_LADDER.filter((px) => px >= (noteFont(fontId).minPx ?? 0)),
    [fontId],
  );
  const sizePx = clampToSizeSteps(sizeWish, sizeSteps);
  const sizeIndex = sizeSteps.indexOf(sizePx);

  const widthDots = printer?.printer.widthDots ?? 384;

  const rendered = useMemo(
    () =>
      renderNote({
        // Until the face is loaded, measuring it would silently measure the
        // fallback — so nothing is drawn in it yet.
        text: fontReady ? text : "",
        font: noteFont(fontId),
        orientation,
        widthDots,
        maxPx: sizePx,
      }),
    [text, fontId, orientation, widthDots, fontReady, sizePx],
  );

  const empty = text.trim().length === 0;

  /**
   * Auto-fit came down below what was asked for, because the larger rungs did
   * not fit across the paper. Asking for MORE cannot help — those are the very
   * rungs that failed — so "Bigger" goes quiet and the line below says why,
   * rather than leaving a control that looks alive and does nothing.
   */
  const shrunkToFit = rendered.layout.px < sizePx;
  const canGoBigger = sizeIndex > 0 && !shrunkToFit;
  const canGoSmaller = sizeIndex >= 0 && sizeIndex < sizeSteps.length - 1;

  const stepSizeTo = (index: number) => {
    const next = sizeSteps[index];
    if (next === undefined) return;
    setSizeWish(next);
    setDone(null);
  };

  const handlePrint = async () => {
    if (empty || !printer) return;
    setBusy("print");
    setError(null);
    setDone(null);
    try {
      // The SAME `printer.printer` that supplied the row width and the heat
      // below also receives the bytes. A role-based print used to re-resolve
      // the registry here and could land them on a different machine — see the
      // note where `printTo` used to live.
      await printRaster(
        printer.printer,
        toCatPrinterJob(rendered.bitmap, {
          ...tuningToJobOptions(printer.printer.tuning),
          rowBytes: Math.ceil(printer.printer.widthDots / 8),
        }),
      );
      // The message stays in the box on purpose — a second copy, or a typo
      // spotted once it is on paper, should not mean typing it again.
      setDone(translate("note.printed"));
    } catch (err: unknown) {
      setError(
        errorText(err, "note.couldNotPrint"),
      );
    } finally {
      setBusy(null);
    }
  };

  /*
   * G-107 (owner, 2026-09-24): the screen was four labelled blocks of grey
   * pills stacked under the Print button — "vibecoded" was the owner's word.
   * It is now two columns with ONE job each:
   *
   *   left  — the words, and a toolbar fixed to the bottom edge of the box
   *           for everything that changes how the WORDS look (face, size);
   *   right — the paper: the card shape above it, the true-size sticker, and
   *           Print under it, because you print what you are looking at.
   *
   * Orientation sits over the preview, not in the text toolbar, because it is
   * a property of the card rather than of the lettering — and because the
   * compose column is ~316 px on the shop tablet, which holds five 46 px
   * buttons on one line but not seven.
   *
   * The sticker is still TRUE SIZE (owner, same day: "keep true size").
   */
  return (
    <div className="shop-floor-note-panel">
      <header className="sf-note-head">
        <h2 className="sf-note-title">{t("note.title")}</h2>
        {/* G-155: both printers on this counter — pair, see which is which,
            forget — from the one kiosk screen every florist opens. The chips
            replace the "No printer paired" bar that used to sit here; the roll
            reminder moved beside the Print button, where the paper matters. */}
        <ShopFloorPrinterChips
          disabled={busy !== null}
          onChanged={() => {
            setPrinter(resolvePrinterFor("notes"));
            setError(null);
          }}
        />
      </header>

      {error ? <div className="alert error">{error}</div> : null}
      {done ? <div className="alert success">{done}</div> : null}

      <div className="sf-note-workspace">
        <div className="sf-note-compose">
          {/* The box and its toolbar are ONE bordered object: focus lights the
              whole frame, so the controls read as part of the writing surface
              rather than a form section below it. */}
          <div className="sf-note-box">
            {/* The count rides in the text's own corner, not in the toolbar:
                on the shop tablet the compose column is 316 px, and the count
                was the item that pushed the toolbar onto a second row. */}
            <div className="sf-note-inputwrap">
              <textarea
                ref={box}
                className="sf-note-input"
                value={text}
                maxLength={MAX_CHARS}
                rows={3}
                aria-label={t("note.message")}
                placeholder={t("note.messagePlaceholder")}
                onChange={(e) => {
                  setText(e.target.value);
                  setDone(null);
                }}
                disabled={busy !== null}
              />
              <span className="sf-note-count muted">
                {text.length}/{MAX_CHARS}
              </span>
            </div>
            <div className="sf-note-toolbar">
              {/* Each face is offered IN that face: the choice is how it looks,
                  so the button shows how it looks. The name stays as the
                  accessible label and the tooltip. */}
              <div
                className="sf-note-tools"
                role="radiogroup"
                aria-label={t("note.fontAria")}
              >
                {NOTE_FONTS.map((f) => {
                  const on = f.id === fontId;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      aria-label={t(f.labelKey)}
                      title={t(f.labelKey)}
                      className={on ? "sf-note-tool is-on" : "sf-note-tool"}
                      style={{ fontFamily: f.stack, fontWeight: f.weight }}
                      onClick={() => setFontId(f.id)}
                      disabled={busy !== null}
                    >
                      <span
                        className={
                          f.webFont ? "sf-note-sample is-hand" : "sf-note-sample"
                        }
                      >
                        {t("note.fontSample")}
                      </span>
                    </button>
                  );
                })}
              </div>

              <span className="sf-note-toolsep" aria-hidden="true" />

              {/* A stepper, not a choice: neither button has a selected state,
                  and at the end of the ladder one simply stops. */}
              <div
                className="sf-note-tools"
                role="group"
                aria-label={t("note.textSizeAria")}
              >
                <button
                  type="button"
                  className="sf-note-tool sf-note-sizestep is-down"
                  aria-label={t("note.smaller")}
                  title={t("note.smaller")}
                  onClick={() => stepSizeTo(sizeIndex + 1)}
                  disabled={busy !== null || !canGoSmaller}
                >
                  {t("note.smallerMark")}
                </button>
                <button
                  type="button"
                  className="sf-note-tool sf-note-sizestep is-up"
                  aria-label={t("note.bigger")}
                  title={t("note.bigger")}
                  onClick={() => stepSizeTo(sizeIndex - 1)}
                  disabled={busy !== null || !canGoBigger}
                >
                  {t("note.biggerMark")}
                </button>
              </div>

            </div>
          </div>

          {/* Says why "Bigger" has gone quiet, so a control that cannot help is
              explained rather than merely dead (G-27 interaction rule 5). */}
          {shrunkToFit ? (
            <p className="muted sf-note-sizenote">{t("note.shrunkToFit")}</p>
          ) : null}

          {/* OUTSIDE the box, deliberately. A button inside the text frame
              would sit under the florist's typing hand, and tapping near the
              textarea throws up the on-screen keyboard — which takes ~55% of
              this screen (G-65 slice 3, measured on the real tablet). The one
              gesture that exists to avoid the keyboard must not summon it.

              The language select is a VISIBLE control, and only on this
              screen: here the words are a customer's, so the language is a
              decision being made rather than a default being accepted. */}
          {dictationSupported() ? (
            <div className="sf-note-dictation">
              <DictationButton
                value={text}
                onChange={(next) => {
                  setText(next);
                  // Same as typing: a printed banner must not outlive the
                  // message it described.
                  setDone(null);
                }}
                locale={dictationLocale}
                ariaLabel={t("note.messageAria")}
                maxLength={MAX_CHARS}
                disabled={busy !== null}
              />
              <label className="sf-note-dictlang">
                <span className="muted">{t("note.dictatingIn")}</span>
                <Select
                  value={dictationLocale}
                  aria-label={t("note.dictationLanguageAria")}
                  disabled={busy !== null}
                  onChange={(e) => {
                    const next = e.target.value;
                    if (!isDictationLocale(next)) return;
                    setDictationLocale(next);
                    rememberDictationLocale(next);
                  }}
                >
                  {DICTATION_LOCALES.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.label}
                    </option>
                  ))}
                </Select>
              </label>
            </div>
          ) : null}
        </div>

        <div className="sf-note-previewpane">
          <div className="sf-note-previewhead">
            {/* Orientation is the florist's call, not ours, because only they
                can see the card — so it is a visible toggle, drawn as the two
                card shapes, sitting on the paper it changes. */}
            <div
              className="sf-note-tools"
              role="radiogroup"
              aria-label={t("note.orientationAria")}
            >
              {ORIENTATIONS.map((o) => {
                const on = o.value === orientation;
                return (
                  <button
                    key={o.value}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    aria-label={t(o.labelKey)}
                    title={t(o.labelKey)}
                    className={on ? "sf-note-tool is-on" : "sf-note-tool"}
                    onClick={() => setOrientation(o.value)}
                    disabled={busy !== null}
                  >
                    <CardGlyph orientation={o.value} />
                  </button>
                );
              })}
            </div>
            <span className="muted sf-note-size">
              {empty
                ? t("note.widthOnly")
                : t("note.sizeWidthByLength", {
                    length: (rendered.lengthMm / 10).toFixed(1),
                  })}
            </span>
          </div>

          {rendered.layout.overflows ? (
            /* Never silently clipped: the packing would truncate this without
               a word, and a florist would find out from the paper. */
            <div className="alert error sf-note-overflow">
              {t("note.overflows")}
            </div>
          ) : null}

          {!fontReady && !empty ? (
            <p className="muted sf-note-empty">{t("note.fontLoading")}</p>
          ) : empty ? (
            <p className="muted sf-note-empty">{t("note.emptyPreview")}</p>
          ) : (
            <NotePreview bitmap={rendered.bitmap} />
          )}

          <div className="sf-note-actions">
            {/* One printer doing both jobs is the NORMAL setup here, so this is
                a reminder about the paper, not a warning about a mistake — one
                quiet line, with the reason on hover/long-press. With nothing
                paired it says so; the chips in the header are the fix. */}
            {!printer ? (
              <span className="muted sf-note-printerline">{t("printer.nonePaired")}</span>
            ) : printer.shared ? (
              <span
                className="muted sf-note-printerline"
                title={t("note.printerLineHint")}
              >
                {t("note.printerLine", {
                  printer: printer.printer.name ?? t("note.thePrinter"),
                })}
              </span>
            ) : null}
            <button
              type="button"
              className="button sf-note-print"
              onClick={() => void handlePrint()}
              disabled={busy !== null || empty || !printer || !fontReady}
            >
              <ShopFloorIcon name="pen" size={22} />
              <span>
                {busy === "print" ? t("printer.printing") : t("note.printButton")}
              </span>
            </button>
            {/* Nothing is destroyed without saying so: Clear is a plain, quiet
                button set apart from Print, never beside it at the same weight. */}
            <button
              type="button"
              className="button plain sf-note-clear"
              onClick={() => {
                setText("");
                setDone(null);
                box.current?.focus();
              }}
              disabled={busy !== null || empty}
            >
              {t("note.clear")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
